import React, { useCallback, useEffect, useMemo, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import {
  getDealerAdminInquiries,
  getDealerAdminInspections,
  getDealerAdminPurchases,
  updateDealerAdminInquiry,
  formatUsd,
  PURCHASE_BOARD_COLUMNS,
  INQUIRY_TYPE_LABELS,
} from "../../../services/dealerGarage";
import { PaymentChip, VehicleThumb } from "./PurchasePanel";
import {
  vehicleLabel, customerName, daysInStep, formatDate, formatPhone, ageLabel, hoursSince,
  unansweredReply, calledSinceReply, needsCall, errorMessage, CALL_AFTER_HOURS,
} from "./dealerAdmin";

// A card that has sat in one column longer than this is flagged.
const STALE_DAYS = 10;

const Days = ({ record }) => {
  const days = daysInStep(record);
  if (days === null) return null;
  return <span className={days >= STALE_DAYS ? styles.stale : undefined}>{days === 0 ? "today" : `${days}d`}</span>;
};

// A customer reply unanswered this long turns the card red.
const REPLY_ALERT_HOURS = 48;

const lastMessage = (inquiry) => {
  const messages = inquiry.messages || [];
  return messages[messages.length - 1] || null;
};

// A lead we have replied to is our turn again when the customer wrote last.
const ourTurn = (inquiry) => {
  const last = lastMessage(inquiry);
  return last ? last.author === "customer" : inquiry.staffUnread > 0;
};

const sinceLabel = (value) => {
  const age = ageLabel(value);
  return age === "today" ? "since today" : age;
};

const agoLabel = (value) => {
  const age = ageLabel(value);
  return age === "today" ? "today" : `${age} ago`;
};

// What a lead or inspection is waiting on, as chips: the dealer in Japan, or
// the deposit we asked for.
const WaitChips = ({ record, wrap }) => {
  const ask = record.dealerAsk || {};
  const dep = record.depositRequest || {};
  if (!ask.askedAt && !dep.requestedAt) return null;
  const Wrap = wrap ? "div" : React.Fragment;
  return (
    <Wrap {...(wrap ? { className: styles.cardMeta } : {})}>
      {ask.askedAt && <span className={`${styles.chip} ${styles.chipMuted}`}>Waiting on dealer · {ageLabel(ask.askedAt)}</span>}
      {dep.requestedAt && !dep.paidAt && (
        <span className={`${styles.chip} ${styles.chipWarn}`}>Deposit asked {formatUsd(dep.amount, { cents: false })} · {ageLabel(dep.requestedAt)}</span>
      )}
      {dep.paidAt && <span className={`${styles.chip} ${styles.chipGood}`}>Deposit received</span>}
    </Wrap>
  );
};

// urgent: tinted while it holds cards, for the column that needs us now.
// drop: while a lead is dragged, { active, over, ...drag handlers } on a column
// it can be dropped in (active: it can land here; over: it is over it now).
const Column = ({ title, items, renderCard, empty = "Nothing here", urgent = false, drop }) => {
  const { active, over, ...dropHandlers } = drop || {};
  const className = [
    styles.column,
    urgent && items.length > 0 && styles.columnUrgent,
    active && styles.columnDropTarget,
    over && styles.columnDropOver,
  ].filter(Boolean).join(" ");
  return (
    <div className={className} {...dropHandlers}>
      <div className={styles.columnHead}>
        <span>{title}</span>
        <span className={styles.count}>{items.length}</span>
      </div>
      {items.length === 0 ? <div className={styles.emptyColumn}>{empty}</div> : items.map((item) => renderCard(item))}
    </div>
  );
};

// The inspection columns, left to right: lane key and title.
const INSPECTION_COLUMNS = [
  ["requested", "New requests"],
  ["depositAsked", "Deposit requested"],
  ["accepted", "Accepted - book a date"],
  ["scheduled", "Booked"],
  ["awaiting", "Waiting on customer"],
  ["readyToBuy", "Ready to buy"],
];

// A customer's inspection request with no deposit on their account, none asked
// for and no decision is still just a conversation: its lead stands for it
// under Leads, like any other inquiry. It becomes an inspection card once we
// ask for the deposit from the inspection, or the customer has one on file.
// (Backend models/dealerInspections.js UNPAID_REQUEST is the same rule.)
const requestAsLead = (i) => i.status === "Requested" && !i.createdBy && !!i.inquiryId
  && !(i.depositRequest && i.depositRequest.requestedAt)
  && !(i.decision && i.decision.value)
  && !(i.depositBalance > 0);

/**
 * Every live deal on one screen, left to right: new leads, everyone waiting on
 * our reply, conversations (and the ones gone quiet, to phone), customers we
 * are searching for, inspections from request to decision, then each purchase
 * step from order to hand-off. Click a card to work on it; drag a lead card to
 * New, In conversation or Searching for them to change its status.
 *
 * Props: onOpenInquiry(inquiry), onOpenInspection(id), onOpenPurchase(id),
 *        refreshKey (bump to reload), onRefresh (the Refresh button, so the
 *        page's counters reload with the board; without it, just the board),
 *        onChanged (a lead was dragged to another status, for the counters)
 */
const PipelineBoard = ({ onOpenInquiry, onOpenInspection, onOpenPurchase, refreshKey, onRefresh, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Most purchase steps are empty most of the time; hidden unless asked for.
  const [showEmpty, setShowEmpty] = useState(false);
  // The lead card being dragged ({ id, status }), and the status column under it.
  const [dragging, setDragging] = useState(null);
  const [dropOver, setDropOver] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      // Archived leads are dead: off the board until the customer stirs.
      getDealerAdminInquiries({ status: "new", archived: false, limit: 200 }),
      getDealerAdminInquiries({ status: "contacted", archived: false, limit: 200 }),
      getDealerAdminInquiries({ status: "searching", archived: false, limit: 200 }),
      getDealerAdminInspections({ limit: 300 }),
      getDealerAdminPurchases({ open: "true", limit: 500 }),
    ])
      .then(([fresh, contacted, searching, inspections, purchases]) => {
        const ok = (res) => (res?.message === "Success" ? res.data : []);
        // An inspection request already has its own card in the Inspections
        // lane; don't show the same lead twice - unless the request is still
        // waiting on a deposit, when the lead is the card.
        const asLeads = new Set(ok(inspections).filter(requestAsLead).map((i) => String(i.inquiryId)));
        const notInspection = (i) => i.type !== "inspection" || !i.vehicleId || asLeads.has(String(i._id));
        setData({
          // One list, split into columns by status, so a dragged lead moves by
          // changing its status alone.
          leads: [...ok(fresh).filter(notInspection), ...ok(contacted).filter(notInspection), ...ok(searching)],
          inspections: ok(inspections),
          purchases: ok(purchases),
        });
      })
      .catch(() => setData({ leads: [], inspections: [], purchases: [] }))
      .finally(() => setLoading(false));
  }, []);

  const patchLead = (id, patch) =>
    setData((d) => ({ ...d, leads: d.leads.map((lead) => (lead._id === id ? { ...lead, ...patch } : lead)) }));

  // Moves the card at once and saves the status; puts it back if that fails.
  // A status change emails no one - it only stamps the lead's timeline.
  const moveLead = async (id, status) => {
    const lead = data.leads.find((l) => l._id === id);
    if (!lead || lead.status === status) return;
    patchLead(id, { status });
    const res = await updateDealerAdminInquiry(id, { status }).catch(() => null);
    if (res?.message === "Inquiry updated" && res.data) {
      patchLead(id, { status: res.data.status, contactedAt: res.data.contactedAt, updatedAt: res.data.updatedAt });
      if (onChanged) onChanged(res.data);
    } else {
      patchLead(id, { status: lead.status });
      alert(errorMessage(res, "Could not move the lead."));
    }
  };

  const endDrag = () => {
    setDragging(null);
    setDropOver(null);
  };

  // The drop props for the column that stands for `status`, while a lead is
  // dragged; none for the column the lead is already in.
  const dropInto = (status) => {
    if (!dragging || dragging.status === status) return undefined;
    return {
      active: true,
      over: dropOver === status,
      onDragOver: (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dropOver !== status) setDropOver(status);
      },
      onDragLeave: (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDropOver(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        moveLead(dragging.id, status);
        endDrag();
      },
    };
  };

  useEffect(() => { load(); }, [load, refreshKey]);

  const lanes = useMemo(() => {
    if (!data) return null;
    const insp = data.inspections;
    const depositPending = (i) => i.depositRequest && i.depositRequest.requestedAt && !i.depositRequest.paidAt;
    // Buying with no sale started yet - inspected or not - shows only under
    // "Ready to buy", where the next move is.
    const buying = (i) => i.decision && i.decision.value === "buy" && !i.purchaseId;
    const open = insp.filter((i) => !buying(i) && !requestAsLead(i));
    const inspectionLanes = {
      requested: open.filter((i) => i.status === "Requested" && !depositPending(i)),
      depositAsked: open.filter((i) => i.status === "Requested" && depositPending(i)),
      accepted: open.filter((i) => i.status === "Accepted"),
      scheduled: open.filter((i) => i.status === "Scheduled"),
      awaiting: open.filter((i) => i.status === "Completed" && !(i.decision && i.decision.value)),
      readyToBuy: insp.filter(buying),
    };
    // From the inspection to the sale, a lead's inspection card stands for it
    // (and shows when the customer has replied); a lead card as well put the
    // customer on the board twice.
    const inspected = new Set(Object.values(inspectionLanes).flat().map((i) => String(i.inquiryId || "")).filter(Boolean));
    const notInspected = (lead) => !inspected.has(String(lead._id));
    // The customer wrote last: our move, whatever stage the deal is at. Those
    // cards wait under Needs reply and go back to their column once answered.
    const leadWaiting = (lead) => ["contacted", "searching"].includes(lead.status) && ourTurn(lead);
    const inspectionWaiting = (i) => !!(i.conversation && i.conversation.lastAuthor === "customer");
    const leadsIn = (status) => data.leads.filter((lead) => lead.status === status && notInspected(lead));
    const conversations = leadsIn("contacted");
    // Replied to, latest first (the ones waiting on us show under Needs reply).
    const talking = conversations.filter((i) => !needsCall(i)).sort((a, b) =>
      new Date(b.lastMessageAt || b.updatedAt || 0) - new Date(a.lastMessageAt || a.updatedAt || 0));
    const searching = leadsIn("searching");
    const waitingLead = (from) => (record) => ({ kind: "lead", record, from, since: (lastMessage(record) || {}).createdAt || record.lastMessageAt });
    const needsReply = [
      ...talking.filter(leadWaiting).map(waitingLead("In conversation")),
      ...searching.filter(leadWaiting).map(waitingLead("Searching for them")),
      ...INSPECTION_COLUMNS.flatMap(([lane, title]) => inspectionLanes[lane].filter(inspectionWaiting)
        .map((record) => ({ kind: "inspection", record, from: `Inspection · ${title}`, since: record.conversation.lastAt }))),
    // Waited longest first.
    ].sort((a, b) => new Date(a.since || 0) - new Date(b.since || 0));
    return {
      newLeads: leadsIn("new"),
      needsReply,
      talking: talking.filter((lead) => !leadWaiting(lead)),
      // Our email unanswered for a day: phone them, longest wait first.
      callList: conversations.filter(needsCall).sort((a, b) =>
        new Date(unansweredReply(a).createdAt) - new Date(unansweredReply(b).createdAt)),
      searching: searching.filter((lead) => !leadWaiting(lead)),
      ...Object.fromEntries(Object.entries(inspectionLanes).map(([lane, items]) => [lane, items.filter((i) => !inspectionWaiting(i))])),
      byStep: PURCHASE_BOARD_COLUMNS.filter((s) => s !== "Completed").map((step) => ({
        step,
        items: data.purchases.filter((p) => p.status === step),
      })),
    };
  }, [data]);

  if (!lanes) return <p>{loading ? "Loading the pipeline..." : ""}</p>;

  const hiddenSteps = lanes.byStep.filter(({ items }) => items.length === 0);

  // from: under Needs reply, the column the card goes back to once answered.
  const fromLine = (from) => from && <div className={styles.cardMeta}><span className={styles.hint}>{from}</span></div>;

  // A div, not a button like the other cards: Firefox won't drag a button.
  const leadCard = (inquiry, from) => (
    <div
      key={inquiry._id}
      role="button"
      tabIndex={0}
      draggable
      className={`${styles.card} ${dragging && dragging.id === inquiry._id ? styles.cardDragging : ""}`}
      onClick={() => onOpenInquiry(inquiry)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenInquiry(inquiry);
        }
      }}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(inquiry._id));
        setDragging({ id: inquiry._id, status: inquiry.status });
      }}
      onDragEnd={endDrag}
    >
      <div className={styles.cardBody}>
        {inquiry.vehicleId && <VehicleThumb record={inquiry} className={styles.cardThumb} />}
        <div className={styles.cardText}>
          <div className={styles.cardTitle}>{vehicleLabel(inquiry)}</div>
          <div className={styles.cardMeta}>{customerName(inquiry)}</div>
        </div>
      </div>
      {fromLine(from)}
      {/* The other cars this customer asked about joined this lead. */}
      {(inquiry.extraCars || []).length > 0 && (
        <div className={styles.cardMeta} title={inquiry.extraCars.map(vehicleLabel).join("\n")}>
          <span className={styles.chip}>+{inquiry.extraCars.length} more {inquiry.extraCars.length === 1 ? "car" : "cars"}</span>
        </div>
      )}
      <div className={styles.cardMeta}>
        {INQUIRY_TYPE_LABELS[inquiry.type] || inquiry.type} · <Days record={inquiry} />
        {inquiry.status !== "contacted" && inquiry.staffUnread > 0 && <span className={classes.unreadBadge}>{inquiry.staffUnread} new</span>}
      </div>
      {["contacted", "searching"].includes(inquiry.status) && ourTurn(inquiry) && (
        <div className={styles.cardMeta}>
          {/* Red once the customer has waited two days for us. */}
          <span className={`${styles.chip} ${hoursSince((lastMessage(inquiry) || {}).createdAt) > REPLY_ALERT_HOURS ? styles.chipBad : styles.chipWarn}`}>
            Customer replied - your turn · {ageLabel((lastMessage(inquiry) || {}).createdAt || inquiry.lastMessageAt)}
          </span>
        </div>
      )}
      {needsCall(inquiry) && (
        <div className={styles.cardMeta}>
          <span className={`${styles.chip} ${styles.chipWarn}`}>No reply to our email · {ageLabel(unansweredReply(inquiry).createdAt)}</span>
          <div>{inquiry.phone ? formatPhone(inquiry.phone) : "No phone number on file"}</div>
        </div>
      )}
      {inquiry.status === "contacted" && !ourTurn(inquiry) && !needsCall(inquiry) && !(inquiry.dealerAsk && inquiry.dealerAsk.askedAt) && (
        <div className={styles.cardMeta}>
          <span className={styles.hint}>
            Waiting on them · {sinceLabel(inquiry.lastMessageAt || inquiry.contactedAt)}
            {calledSinceReply(inquiry) ? ` · called ${agoLabel(inquiry.calledAt)}` : ""}
          </span>
        </div>
      )}
      <WaitChips record={inquiry} wrap />
      {inquiry.status === "searching" && inquiry.searchNotes && (
        <div className={styles.cardMeta}>Looking for: {inquiry.searchNotes.slice(0, 80)}</div>
      )}
    </div>
  );

  const inspectionCard = (inspection, from) => (
    <button key={inspection._id} type="button" className={styles.card} onClick={() => onOpenInspection(inspection._id)}>
      <div className={styles.cardBody}>
        <VehicleThumb record={inspection} className={styles.cardThumb} />
        <div className={styles.cardText}>
          <div className={styles.cardTitle}>{vehicleLabel(inspection)}</div>
          <div className={styles.cardMeta}>{customerName(inspection)}</div>
        </div>
      </div>
      {fromLine(from)}
      <div className={styles.cardMeta}>
        {inspection.scheduledFor && inspection.status === "Scheduled" ? `${formatDate(inspection.scheduledFor)} · ` : ""}
        <Days record={inspection} />
        {(inspection.checks || []).length > 0 && ` · ${inspection.checks.length} to check`}
      </div>
      {/* The customer wrote last on the request's conversation: our move. */}
      {inspection.conversation && inspection.conversation.lastAuthor === "customer" && (
        <div className={styles.cardMeta}>
          <span className={`${styles.chip} ${hoursSince(inspection.conversation.lastAt) > REPLY_ALERT_HOURS ? styles.chipBad : styles.chipWarn}`}>
            Customer replied - your turn · {ageLabel(inspection.conversation.lastAt)}
          </span>
        </div>
      )}
      {inspection.status === "Requested" && !(inspection.depositRequest && inspection.depositRequest.requestedAt) && (
        <div className={styles.cardMeta}>
          {inspection.depositBalance > 0
            ? <span className={`${styles.chip} ${styles.chipGood}`}>Deposit {formatUsd(inspection.depositBalance, { cents: false })}</span>
            : <span className={`${styles.chip} ${styles.chipMuted}`}>No deposit yet</span>}
        </div>
      )}
      {["Requested", "Accepted", "Scheduled"].includes(inspection.status) && <WaitChips record={inspection} wrap />}
    </button>
  );

  const replyCard = ({ kind, record, from }) => (kind === "lead" ? leadCard(record, from) : inspectionCard(record, from));

  const purchaseCard = (purchase) => (
    <button key={purchase._id} type="button" className={styles.card} onClick={() => onOpenPurchase(purchase._id)}>
      <div className={styles.cardBody}>
        <VehicleThumb record={purchase} className={styles.cardThumb} />
        <div className={styles.cardText}>
          <div className={styles.cardTitle}>{vehicleLabel(purchase)}</div>
          <div className={styles.cardMeta}>
            {customerName(purchase)}{purchase.price ? ` · ${formatUsd(purchase.price, { cents: false })}` : ""}
          </div>
        </div>
      </div>
      <div className={styles.cardMeta}><PaymentChip billing={purchase.billing} /> <Days record={purchase} /></div>
      {purchase.shippingDetail && purchase.shippingDetail.estimatedArrivalDate && ["Ship Booked", "In Transit"].includes(purchase.status) && (
        <div className={styles.cardMeta}>ETA {formatDate(purchase.shippingDetail.estimatedArrivalDate)}</div>
      )}
      {(purchase.source === "auction" || purchase.status === "In Storage" || (purchase.delivery && purchase.delivery.requestedBy === "customer")) && (
        <div className={styles.cardMeta}>
          {purchase.source === "auction" && <span className={`${styles.chip} ${styles.chipMuted}`}>Auction</span>}
          {purchase.status === "In Storage" && purchase.storage && purchase.storage.eligibleOn && (
            <span className={`${styles.chip} ${styles.chipMuted}`}>Eligible {formatDate(purchase.storage.eligibleOn)}</span>
          )}
          {/* The customer told us how they want it handed over; staff still schedule it. */}
          {purchase.delivery && purchase.delivery.requestedBy === "customer" && !purchase.delivery.scheduledDate && (
            <span className={`${styles.chip} ${styles.chipWarn}`}>{purchase.delivery.method === "delivery" ? "Wants delivery" : "Port pickup"}</span>
          )}
        </div>
      )}
    </button>
  );

  return (
    <div>
      <div className={styles.boardToolbar}>
        <button type="button" className={styles.btn} onClick={onRefresh || load} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
        <label className={styles.checkboxRow} style={{ margin: 0 }}>
          <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} />
          Show empty purchase steps{hiddenSteps.length && !showEmpty ? ` (${hiddenSteps.length} hidden)` : ""}
        </label>
        <span className={styles.hint}>Days in orange: the card has sat in that column {STALE_DAYS}+ days.</span>
        <span className={styles.hint}>Drag a lead to New, In conversation or Searching for them to change its status.</span>
      </div>
      <div className={styles.board}>
        <div className={styles.lane}>
          <div className={styles.laneTitle}>Leads</div>
          <div className={styles.laneColumns}>
            {/* Needs reply and Call list follow the email thread, so a lead
                can be dragged out of them but not into them. */}
            <Column title="New" items={lanes.newLeads} renderCard={leadCard} drop={dropInto("new")} />
            <Column title="Needs reply" items={lanes.needsReply} renderCard={replyCard} urgent empty="Cards land here when a customer writes back, and return once we reply." />
            <Column title="In conversation" items={lanes.talking} renderCard={leadCard} drop={dropInto("contacted")} />
            <Column title="Call list" items={lanes.callList} renderCard={leadCard} empty={`Leads land here when our email goes ${CALL_AFTER_HOURS}h without a reply (not while an inspection is open or a deposit is on file).`} />
            <Column title="Searching for them" items={lanes.searching} renderCard={leadCard} drop={dropInto("searching")} />
          </div>
        </div>
        <div className={styles.lane}>
          <div className={styles.laneTitle}>Inspections</div>
          <div className={styles.laneColumns}>
            {INSPECTION_COLUMNS.map(([lane, title]) => (
              <Column key={lane} title={title} items={lanes[lane]} renderCard={inspectionCard} />
            ))}
          </div>
        </div>
        <div className={styles.lane}>
          <div className={styles.laneTitle}>Purchases</div>
          <div className={styles.laneColumns}>
            {lanes.byStep.filter(({ items }) => showEmpty || items.length > 0).map(({ step, items }) => (
              <Column key={step} title={step} items={items} renderCard={purchaseCard} empty="-" />
            ))}
            {!showEmpty && hiddenSteps.length === lanes.byStep.length && (
              <div className={styles.column}><div className={styles.emptyColumn}>No cars in progress.</div></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineBoard;
